import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import CancelIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Done';
import { Tooltip } from '@mui/material';
import cx from 'classnames';

import { getOrganizationUsersByRole, getUserOrganization, updateUserOrganization, deleteOrgAvatar } from '../../../../api/organizationsApi';
import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { saveMyOrganization } from '../../../../store/user/userSlice';
import { MY_ORGANIZATION, USER_NEW } from '../../../../enums/TYPES';
import UploadPhotoModal from './UploadPhotoModal/UploadPhotoModal';
import RolePlate from '../../../../components/modules/RolePlate';
import { ROLES, RolesLowerCase } from '../../../../enums/ROLES';
import Avatar from '../../../../components/modules/Avatar';
import Modal from '../../../../components/modules/Modal';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import styles from './MyOrgModal.module.scss';

type TMyOrgModal = {
  open: boolean;
  switchModal: () => void;
};

const MyOrgModal: React.FC<TMyOrgModal> = ({ open, switchModal }) => {
  const canViewEmails = useCanViewEmails();
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const { myOrganization, role } = useAppSelector((state) => state.user);
  const [myOrg, setMyOrg] = useState(myOrganization);
  const [oaList, setOaList] = useState<USER_NEW[]>();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const getOaList = async () => {
    const res = await getOrganizationUsersByRole(`${RolesLowerCase.ORGANIZATION_ADMIN}`);
    setOaList(res.data.documents);
  };

  const [hoursUsed, setHoursUsed] = useState('');
  useEffect(() => {
    if (myOrganization.id && role !== ROLES.SUPER_ADMIN) {
      setMyOrg(myOrganization);

      if (myOrganization.hours_limit) {
        const hours = Math.floor(myOrganization.hours_limit_used);
        const minutes = Math.round((myOrganization.hours_limit_used - hours) * 60);
        setHoursUsed(`${hours} hours ${minutes} mins`);
      }
      getOaList();
    }
  }, [myOrganization]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const obj = {
      ...myOrg,
      [e.target.id]: e.target.value,
    };

    if (obj) {
      setMyOrg(obj);
    }
  };
  const onClose = () => {
    switchModal();

    setDeleteModal(false);
  };
  const clickSave = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const key = e.currentTarget.id as keyof MY_ORGANIZATION;
      await updateUserOrganization({ [e.currentTarget.id]: myOrg[key] });
      const org = await getUserOrganization();
      dispatch(saveMyOrganization(org.data));
      notification.showMessage(NOTIFICATION.SAVED);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const clickCancel = (e: React.MouseEvent<HTMLDivElement>) => {
    if (loading) {
      return;
    }
    const key = e.currentTarget.id as keyof MY_ORGANIZATION;
    setMyOrg({
      ...myOrg!,
      [e.currentTarget.id]: myOrganization[key],
    });
  };
  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  const deleteAvatar = async () => {
    try {
      setLoading(true);
      await deleteOrgAvatar();
      const response = await getUserOrganization();
      dispatch(saveMyOrganization(response.data));
      notification.showMessage(NOTIFICATION.DELETED);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setDeleteModal(false);
    }
  };
  const avatarDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  return (
    <Modal open={open} onClose={switchModal} loading={loading}>
      <UploadPhotoModal open={showUploadModal} setOpen={setShowUploadModal} />

      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <Avatar name={myOrganization?.name} className={styles.orgAvatar} avatar={myOrganization.image_url} />

          {role === ROLES.ORGANIZATION_ADMIN && (
            <>
              <div className={styles.uploadBtn1}>
                <ContainedButton className={styles.contained} onClick={openUploadModal}>
                  Upload New
                </ContainedButton>
              </div>
              <div className={styles.uploadBtn2}>
                <OutlinedButton onClick={avatarDeleteModal}>Delete</OutlinedButton>
              </div>
            </>
          )}
        </div>

        <form className={styles.form}>
          {myOrg && (
            <>
              <div className={styles.label}>Name</div>
              <input
                value={myOrg.name}
                className={cx(styles.field, role !== ROLES.ORGANIZATION_ADMIN ? styles.disabledField : '')}
                onChange={onChange}
                id="name"
                type="text"
                disabled={!(role === ROLES.ORGANIZATION_ADMIN)}
              />

              {myOrg.name !== myOrganization?.name && (
                <div className={styles.saveBtnContainer}>
                  <div onClick={clickSave} id="name" className={styles.saveBtn}>
                    <SaveIcon />
                  </div>
                  <div onClick={clickCancel} id="name" className={styles.cancelBtn}>
                    <CancelIcon />
                  </div>
                </div>
              )}
              <div className={styles.label}>Location</div>
              <div className={styles.location}> {myOrg.location} </div>
              <div className={styles.label}>User Limit</div>
              <div className={styles.value}>
                {myOrganization?.total_users} /{myOrg.users_limit}
              </div>
              <div className={styles.label}>Storage Limit</div>
              <div className={styles.value}>{myOrg.storage_limit} GB</div>

              <div className={styles.label}>Hours Limit</div>
              <div className={styles.value}>
                {myOrg?.hours_limit_used?.toFixed(1)} / {myOrg.hours_limit} hours
              </div>

              <div className={styles.label}>Hours limit period</div>
              <div className={styles.value}>
                {myOrg.period_start} - {myOrg.period_end}
              </div>

              {myOrganization.allowedVideoQualities && (
                <>
                  <div className={styles.label}>Allowed Processing</div>
                  <div className={styles.value}>to multiple video qualities</div>
                </>
              )}
            </>
          )}
        </form>

        <div className={styles.oaList}>
          {oaList?.map((oa) => (
            <div key={oa.user.id} className={styles.oaElement}>
              {oa.user.avatar_url ? <Avatar avatar={oa.user.avatar_url} /> : <div className={styles.oaAvatar}>{oa.user.email[0]}</div>}

              {canViewEmails && (<Tooltip title={oa.user.email}>
                <div className={styles.oaEmail}>{oa.user.email}</div>
              </Tooltip>)}

              <RolePlate className={styles.rolePlate} userRole="OA" />
            </div>
          ))}
        </div>
        <form className={styles.form}>
          {myOrg && (
            <>
              <div className={styles.descriptionBg}>
                <div className={styles.descriptionLabel}>Description</div>
                <textarea
                  value={myOrg?.description}
                  className={styles.descriptionField}
                  onChange={onChange}
                  style={{ resize: 'vertical', overflow: 'hidden' }}
                  id="description"
                  cols={33}
                  disabled={!(role === ROLES.ORGANIZATION_ADMIN)}
                  rows={myOrg?.description?.length / 30}
                />
              </div>

              {myOrg.description !== myOrganization?.description && (
                <div className={styles.saveBtnContainer}>
                  <div onClick={clickSave} id="description" className={styles.saveBtn}>
                    <SaveIcon />
                  </div>
                  <div onClick={clickCancel} id="description" className={styles.cancelBtn}>
                    <CancelIcon />
                  </div>
                </div>
              )}
              <div className={styles.actionBtns}>
                <ContainedButton onClick={switchModal} disabled={loading}>
                  Close
                </ContainedButton>
              </div>
            </>
          )}
        </form>
      </div>
      <Modal open={deleteModal} onClose={onClose} loading={loading}>
        <div className={styles.deleteModalBody}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>Are you sure u want to delete avatar ?</div>
          </div>
          <div className={styles.deleteuploadBtn1}>
            <ContainedButton className={styles.btnWidth} disabled={myOrg?.image_url === ''} onClick={deleteAvatar}>
              Yes
            </ContainedButton>
          </div>
          <div className={styles.deleteuploadBtn2}>
            <OutlinedButton onClick={avatarDeleteModal}>No</OutlinedButton>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default MyOrgModal;
